import React, { useCallback, useEffect, useMemo, useReducer } from 'react';
import { useDropzone } from 'react-dropzone';
import { Attachment } from '@material-ui/icons';
import AlignHorizontalLeftIcon from '@mui/icons-material/AlignHorizontalLeft';
import AlignHorizontalCenterIcon from '@mui/icons-material/AlignHorizontalCenter';
import AlignHorizontalRightIcon from '@mui/icons-material/AlignHorizontalRight';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import './ImageComponent.scss';
import CustomSelect from '../../../../shared/form-controls/custom-select/Custom-Select';
import { URL_LIST } from '../../../../constants/EmailServicesConst';
import CustomTextField from '../../../../shared/form-controls/custom-text-field/Custom-Text-Field';
import PaddingComponent from '../../../../shared/email-services/padding-component/PaddingComponent';
import DividerLine from '../../../../shared/email-services/divider-line/DividerLine';
import BackgroundColor from '../../../../shared/email-services/background-color/BackgroundColor';
import { formDataValidate, handleValidation } from '../../../../utils/formUtils';
import { getPreSignedUrl } from '../../../../utils/PropertyAPIUtils';
import { ASSET_TYPE_PROPERTY } from '../../../../constants/PropertyConst';
import { ASSET_URL, ENVIRONMENT } from '../../../../env/Configurations';
import { convertFileToBase64 } from '../../../../utils/commonUtils';
import { notificationManager } from '../../../../utils/NotificationUtils';

const baseStyle = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderWidth: 1,
  borderRadius: 10,
  borderColor: '#CCCCCC',
  borderStyle: 'dashed',
  backgroundColor: '#E8F0F7',
  color: '#CCCCCC',
  outline: 'none',
  transition: 'border .24s ease-in-out',
  minHeight: 120,
};

const ImageComponent = (props) => {
  const {
    onItemChange,
    isRemovable,
    componentObj,
    componentId,
    subId,
    activeNdx,
    isHex,
    onRemoveComponent,
    groupId,
  } = props;
  const [formData, setFormData] = useReducer((state, newState) => ({ ...state, ...newState }), {
    image: {
      value: '',
      fieldName: 'image',
      errors: {},
      imageDetails: {
        maxSize: 10485760,
        uploadSize: 0,
        height: 768,
        width: 936,
        uploadHeight: 0,
        uploadwidth: 0,
      },
      validations: ['validImageMaxSize', 'validResolution'],
      tab: 0,
    },
    url: {
      value: '',
      fieldName: 'URL',
      errors: {},
      validations: ['validUrl', 'safeDomain'],
      tab: 0,
    },
    urlLink: {
      value: '',
      fieldName: 'Enter Link',
      errors: {},
      validations: ['validUrl', 'safeDomain'],
      tab: 0,
    },
    alignment: {
      value: 'center',
      fieldName: 'alignment',
      errors: {},
      validations: [],
      tab: 0,
    },
    width: {
      value: '200',
      fieldName: 'Width',
      errors: {},
      validations: [],
      tab: 0,
    },
    height: {
      value: '200',
      fieldName: 'Height',
      errors: {},
      validations: [],
      tab: 0,
    },
    padding: {
      value: false,
      fieldName: 'Padding',
      errors: {},
      validations: [],
      tab: 0,
    },
    top: {
      value: '10',
      fieldName: 'Top',
      errors: {},
      validations: [],
      tab: 0,
    },
    bottom: {
      value: '10',
      fieldName: 'Bottom',
      errors: {},
      validations: [],
      tab: 0,
    },
    left: {
      value: '10',
      fieldName: 'Left',
      errors: {},
      validations: [],
      tab: 0,
    },
    right: {
      value: '10',
      fieldName: 'Right',
      errors: {},
      validations: [],
      tab: 0,
    },
    hexCode: {
      value: '#82828214',
      fieldName: 'Hex Code',
      errors: {},
      validations: [],
      tab: 0,
    },
  });

  const validate = () => {
    const isValid = formDataValidate(formData, false);
    return isValid;
  };

  useEffect(() => {
    const currFields = { ...formData };
    if (componentObj && Object.keys(componentObj).length > 0) {
      for (let field in currFields) {
        if (componentObj[field] !== undefined) currFields[field].value = componentObj[field];
      }
    }

    setFormData(currFields);
  }, [activeNdx]);

  useEffect(() => {
    onItemChange({
      id: componentId,
      subId: subId || null,
      image: formData.image.value,
      url: formData.url.value,
      urlLink: formData.urlLink.value,
      alignment: formData.alignment.value,
      width: Number(formData.width.value),
      height: Number(formData.height.value),
      padding: formData.padding.value,
      top: Number(formData.top.value),
      bottom: Number(formData.bottom.value),
      left: Number(formData.left.value),
      right: Number(formData.right.value),
      hexCode: formData.hexCode.value,
      index: componentObj?.index > -1 ? componentObj?.index : -1,
      isValid: validate(),
    });
  }, [formData]);

  const setImageError = (errorMessage, validateValue) => {
    let imageDetails = { ...formData.image.imageDetails };
    if (validateValue?.resolution) {
      imageDetails.uploadHeight = validateValue.resolution.uploadHeight;
      imageDetails.uploadwidth = validateValue.resolution.uploadwidth;
    } else {
      imageDetails[validateValue?.key] = validateValue?.value;
    }
    setFormData({
      image: {
        ...formData.image,
        value: '',
        imageDetails,
        errors: { ...errorMessage },
      },
    });
  };

  const onDropRejected = useCallback((rejectedFiles) => {
    return setImageError(
      {
        validFormat:
          rejectedFiles && rejectedFiles.length > 0
            ? 'Please add images with supported file formats'
            : null,
      },
      null,
    );
  }, []);

  const uploadImage = async (input, fileObj) => {
    try {
      if (fileObj) {
        convertFileToBase64(fileObj)
          .then(async (base64) => {
            input['contents'] = base64;

            try {
              const res = await getPreSignedUrl(input);

              if (res?.data?.getPreSignUrlCMS?.key) {
                setFormData({
                  image: {
                    ...formData.image,
                    value: res.data.getPreSignUrlCMS.key,
                    imageDetails: {
                      ...formData.image.imageDetails,
                      uploadSize: 0,
                      uploadHeight: 0,
                      uploadwidth: 0,
                    },
                    errors: {},
                  },
                });
              } else {
                setFormData({
                  image: {
                    ...formData.image,
                    value: '',
                    errors: {
                      uploadError: 'Please add images with supported file formats',
                    },
                  },
                });
              }
            } catch (exception) {
              if (ENVIRONMENT === 'dev') {
                console.log(exception?.message);
              }

              if (exception?.message) {
                notificationManager('danger', exception?.message.replace('GraphQL error:', ''));
              } else {
                notificationManager('danger', 'Error while uploading file.');
              }
            }
          })
          .catch((error) => {
            if (ENVIRONMENT === 'dev') {
              console.log(error?.message, 'error');
            }

            notificationManager('danger', 'Error converting file to base64');
          });
      }
    } catch (err) {
      setFormData({
        image: {
          ...formData.image,
          value: '',
          errors: { uploadError: 'Please add images with supported file formats' },
        },
      });
    }
  };

  const onDropAccepted = useCallback((acceptedFiles) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      if (acceptedFiles[0].size > 10485761)
        return setImageError(
          {
            validMaxSize: 'Sorry! Unable to add Image with size more than 10MB',
          },
          { key: 'uploadSize', value: acceptedFiles[0].size },
        );
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        let image = document.createElement('img');
        image['src'] = reader.result;
        var imageObj = new Image();
        imageObj.src = reader.result;
        imageObj.onload = () => {
          if (imageObj.height > 769 || imageObj.width > 937) {
            return setImageError(
              {
                validResolution: 'Sorry! Unable to add Image with dimension more than 936 x 768',
              },
              {
                resolution: {
                  uploadHeight: imageObj.height,
                  uploadwidth: imageObj.width,
                },
              },
            );
          }
          const input = {
            type: ASSET_TYPE_PROPERTY,
            groupId: groupId,
            propertyType: 'email-services',
            contentLength: acceptedFiles[0].size,
            contentType: acceptedFiles[0].type,
            filename: acceptedFiles[0].name,
          };
          uploadImage(input, acceptedFiles[0]);
        };
      });
      reader.readAsDataURL(acceptedFiles[0]);
    }
  }, []);

  const activeStyle = {
    borderColor: '#2196f3',
  };

  const acceptStyle = {
    borderColor: '#00e676',
  };

  const rejectStyle = {
    borderColor: '#ff1744',
  };

  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject, open } =
    useDropzone({
      accept: 'image/png, image/jpg, image/jpeg',
      multiple: false,
      noClick: true,
      onDropAccepted,
      onDropRejected,
    });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isDragActive, isDragReject, isDragAccept],
  );

  const onClickAdromentValue = () => {
    const urlLink = { ...formData.urlLink };
    const url = { ...formData.url };
    urlLink.value = '';
    urlLink.validations = [];
    url.value = '';
    setFormData({ urlLink, url });
  };

  const onChangeUrl = (e) => {
    const urlLink = { ...formData.urlLink };
    urlLink.validations = ['validUrl'];
    setFormData({ urlLink, url: e });
  };

  useEffect(() => {
    if (formData?.url?.value) {
      handleValidation(formData?.urlLink);
    }
  }, []);

  const renderError = () => {
    if (Object.keys(formData.image.errors).length > 0)
      return Object.keys(formData.image.errors).map((errorVal) => (
        <div className='input-error mt-4' key={errorVal}>
          {formData.image.errors[errorVal]}
        </div>
      ));
  };

  return (
    <div className='image-component'>
      <div className='d-flex justify-content-between'>
        <p className='edit-image'>Edit Image</p>
        {isRemovable && (
          <p className='edit-image remove-text' onClick={() => onRemoveComponent()}>
            Remove
          </p>
        )}
      </div>
      <div className='image-component-drop-zone'>
        {formData.image.value ? (
          <div className='image-component-display'>
            <img src={ASSET_URL + formData.image.value} className='display-image' />
          </div>
        ) : (
          <div {...getRootProps({ style })}>
            <input {...getInputProps()} />
            <div className='d-flex align-items-center'>
              <Attachment />
              <span className='ms-1'>
                Drag and drop or{' '}
                <span className='text-highlight pointer' onClick={open}>
                  Browse
                </span>
              </span>
            </div>
          </div>
        )}
      </div>
      <div className='d-flex justify-content-between'>
        <div className='image-component-details'>
          File type: .JPG, .PNG, <br />
          936 x 768 pixels, Max file size: 10MB
        </div>
        {formData.image.value && (
          <>
            <input {...getInputProps()} />
            <div className='image-component-details replace-text' onClick={open}>
              Replace
            </div>
          </>
        )}
      </div>
      {renderError()}
      <div className='mt-3'>
        <CustomSelect
          valueField='id'
          labelField='name'
          item={formData.url}
          handleInputChange={(e) => onChangeUrl(e)}
          options={URL_LIST}
          hideNullOption={true}
        />
      </div>
      {formData.url.value && (
        <div className='mt-3'>
          <CustomTextField
            item={formData.urlLink}
            handleInputChange={(e) => {
              setFormData({ urlLink: e });
            }}
            maxLength={1000}
            endAdornment={true}
            adromentValue='Remove'
            onClickAdromentValue={() => onClickAdromentValue()}
          />
        </div>
      )}
      <div className='row mt-3'>
        <div className='col-md-6'>Alignment</div>
        <div className='col-md-2'>
          <span
            onClick={() =>
              setFormData({
                alignment: { ...formData.alignment, value: 'left' },
              })
            }
          >
            <AlignHorizontalLeftIcon
              className={`pointer ${
                formData.alignment.value === 'left' ? 'image-component-alignment' : ''
              }`}
            />
          </span>
        </div>
        <div className='col-md-2'>
          <span
            onClick={() =>
              setFormData({
                alignment: { ...formData.alignment, value: 'center' },
              })
            }
          >
            <AlignHorizontalCenterIcon
              className={`pointer ${
                formData.alignment.value === 'center' ? 'image-component-alignment' : ''
              }`}
            />
          </span>
        </div>
        <div className='col-md-2'>
          <span
            onClick={() =>
              setFormData({
                alignment: { ...formData.alignment, value: 'right' },
              })
            }
          >
            <AlignHorizontalRightIcon
              className={`pointer ${
                formData.alignment.value === 'right' ? 'image-component-alignment' : ''
              }`}
            />
          </span>
        </div>
      </div>
      <div className='d-flex align-items-center mt-3'>
        <CustomTextField
          item={formData.width}
          handleInputChange={(e) => {
            setFormData({ width: e });
          }}
          type='number'
        />
        <InsertLinkIcon className='image-component-rotate' />
        <CustomTextField
          item={formData.height}
          handleInputChange={(e) => {
            setFormData({ height: e });
          }}
          type='number'
        />
      </div>
      <div className='mt-3'>
        <PaddingComponent formData={formData} setFormData={setFormData} />
      </div>
      <DividerLine />
      {isHex && (
        <div className='mt-5'>
          <BackgroundColor
            formData={formData}
            setFormData={setFormData}
            title='Background Color'
            fieldKey='hexCode'
          />
        </div>
      )}
    </div>
  );
};

export default ImageComponent;
